import React, { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import _ from "lodash";

import {
  loadWebContent,
  changeWebContent,
  resetToSave,
  getWebFilteringPreview,
  changeShowMessageToSave,
  changeMessageToSave,
  changeAdminEmailToSave,
  changeAdminPhoneToSave,
  changeShowCodeToSave,
  changeShowNameToSave,
} from "./webContent.actions";
import { TabControls } from "../../components/tabControls/TabControls";
import { withPromptDialog } from "../../../../hocs/withPrompt";
import { BlockingMessage } from "../../components/blockingMessage/BlockingMessage";
import { DeviceRegistration } from "./DeviceRegistration";
import { AdminInfo } from "../../components/adminInfo/AdminInfo";
import { useMount } from "@sportal/cdk/hooks";
import {
  getFromWebContentValidation,
  isWebContentFetched,
} from "./webContent.selectors";
import { useWebFilteringForm } from "./useWebFilteringForm";
import { isMultipleUserLevel } from "../../../../store/account";
import { previewPage } from "../shared.actions";
import { Form } from "../../../../components/form";
import { Desktop, TabletMobile } from "../../../../hocs/Responsive";
import { AriaPageTitle } from "../../../../components/ariaPageTitle";

const WebFilteringFormContent = withPromptDialog(
  ({ hasChanges, save, discard }) => {
    const dispatch = useDispatch();

    const isFetched = useSelector(isWebContentFetched);
    const isMultiple = useSelector(isMultipleUserLevel);
    const rules = useSelector(getFromWebContentValidation("rules"), _.isEqual);

    const { isValid, formValues, isEmailPatternValid } = useWebFilteringForm();

    useMount(() => {
      !isFetched && dispatch(loadWebContent());
    });

    const preview = async () =>
      await previewPage(dispatch, getWebFilteringPreview(), "web-filtering");

    return (
      <section className="web-filtering-form">
        <AriaPageTitle pageKey="page_title_service_page_webfilter" />
        <Desktop>
          <h1 className={"blocked-page__title"}>
            <FormattedMessage id={"web_filtering"} />
          </h1>
        </Desktop>
        <TabletMobile>
          <h2 className={"blocked-page__title"}>
            <FormattedMessage id={"web_filtering"} />
          </h2>
        </TabletMobile>
        <section className={"blocked-page__description"}>
          <FormattedMessage id={"web_filtering_description"} />
        </section>
        <Form
          onSubmit={save}
          name={"webFilteringForm"}
          description={
            <FormattedMessage id={"web_filtering_form_description"} />
          }
          hiddenDescription
        >
          <BlockingMessage
            showMessage={formValues.showMessage}
            toggleMessage={() => dispatch(changeShowMessageToSave())}
            changeMessage={(e) => dispatch(changeMessageToSave(e.target.value))}
            message={formValues.message}
            messageValidation={rules.message}
          />
          <AdminInfo
            email={{
              value: formValues.adminEmail,
              action: (adminEmail) =>
                dispatch(changeAdminEmailToSave(adminEmail)),
              validation: rules.adminEmail,
              isEmailPatternValid,
            }}
            phone={{
              value: formValues.adminPhone,
              action: (adminPhone) =>
                dispatch(changeAdminPhoneToSave(adminPhone)),
              validation: rules.adminPhone,
            }}
          />
          {isMultiple && (
            <DeviceRegistration
              showCode={formValues.showCode}
              showName={formValues.showName}
              changeShowCode={() => dispatch(changeShowCodeToSave())}
              changeShowName={() => dispatch(changeShowNameToSave())}
            />
          )}

          <TabControls
            isFormValid={isValid}
            isFormUnchanged={!hasChanges}
            onReset={discard}
            onPreview={preview}
          />
        </Form>
      </section>
    );
  }
);

WebFilteringFormContent.propTypes = {
  hasChanges: PropTypes.bool,
  save: PropTypes.func,
  discard: PropTypes.func,
};

export const WebFilteringForm = () => {
  const dispatch = useDispatch();
  const { hasChanges, isValid } = useWebFilteringForm();

  const save = () => dispatch(changeWebContent());
  const discard = useCallback(() => dispatch(resetToSave()), [dispatch]);

  useEffect(() => {
    return () => discard();
  }, [discard]);

  return (
    <WebFilteringFormContent
      save={save}
      discard={discard}
      hasChanges={hasChanges && isValid}
    />
  );
};
